import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';

const CartDrawer = () => {
  const {
    items,
    isCartOpen,
    setIsCartOpen,
    updateQuantity,
    removeFromCart,
    cartCount,
    subtotal,
    discountAmount,
    discountSummary,
    freeShippingUnlocked,
    freeShippingReason,
    promoCodeInput,
    setPromoCodeInput,
    appliedPromoRule,
    promoFeedback,
    applyPromoCode,
    removePromoCode,
    requiresShipping,
    shippingAmount,
    freeShippingThreshold,
    freeShippingRemaining,
    activeTaxZone,
    taxAmount,
    totalAmount,
    formatPrice
  } = useCart();

  const navigate = useNavigate();

  if (!isCartOpen) return null;

  const handleProceedToCheckout = () => {
    setIsCartOpen(false);
    navigate('/checkout');
  };

  const handlePromoSubmit = (e) => {
    if (e && e.preventDefault) e.preventDefault();
    applyPromoCode(promoCodeInput);
  };

  return (
    <div
      className="fixed inset-0 z-50 overflow-hidden"
      aria-labelledby="shopping-cart-title"
      role="dialog"
      aria-modal="true"
      data-testid="cart-drawer"
    >
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-gray-900/60 backdrop-blur-xs transition-opacity"
        onClick={() => setIsCartOpen(false)}
      />

      <div className="fixed inset-y-0 right-0 max-w-full flex pl-10">
        <div className="w-screen max-w-md bg-white shadow-2xl flex flex-col justify-between">
          {/* Header */}
          <div className="p-5 bg-gradient-to-r from-green-700 to-green-600 text-white flex items-center justify-between shadow-sm">
            <div className="flex items-center gap-2.5">
              <span className="text-2xl" aria-hidden="true">🛒</span>
              <div>
                <h2 id="shopping-cart-title" className="text-lg font-bold leading-tight">
                  Your Shopping Cart
                </h2>
                <p className="text-xs text-green-100">
                  {cartCount} {cartCount === 1 ? 'item' : 'items'} selected
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setIsCartOpen(false)}
              className="p-2 rounded-full bg-white/15 hover:bg-white/25 text-white transition focus:outline-none cursor-pointer"
              aria-label="Close shopping cart"
              data-testid="close-cart-drawer"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Free Shipping Progress Bar */}
          {items.length > 0 && requiresShipping && (freeShippingThreshold > 0 || freeShippingUnlocked) && (
            <div className="bg-yellow-50 border-b border-yellow-200 px-5 py-3">
              {freeShippingRemaining > 0 && !freeShippingUnlocked ? (
                <div>
                  <p className="text-xs font-semibold text-green-900 mb-1.5">
                    Add <span className="text-green-700 font-bold">{formatPrice(freeShippingRemaining)}</span> more to unlock{' '}
                    <span className="underline">FREE Shipping</span>!
                  </p>
                  <div className="w-full h-2 bg-yellow-200 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-green-600 transition-all duration-300"
                      style={{ width: `${Math.min(100, (subtotal / freeShippingThreshold) * 100)}%` }}
                    />
                  </div>
                </div>
              ) : (
                <p className="text-xs font-bold text-green-800 flex items-center gap-1.5" data-testid="cart-free-shipping-unlocked">
                  <span>🎉</span>{' '}
                  {freeShippingReason
                    ? `FREE Shipping unlocked (${freeShippingReason})!`
                    : "You've unlocked FREE Standard Shipping!"}
                </p>
              )}
            </div>
          )}

          {/* Cart Items List */}
          <div className="flex-1 overflow-y-auto p-5 divide-y divide-gray-100">
            {items.length === 0 ? (
              <div className="h-full flex flex-col items-center justify-center text-center py-12">
                <div className="w-20 h-20 rounded-full bg-green-50 flex items-center justify-center text-4xl mb-4">
                  🎾
                </div>
                <h3 className="text-lg font-bold text-gray-800 mb-1">Your cart is empty</h3>
                <p className="text-sm text-gray-500 max-w-xs mb-6">
                  Explore our tennis logbooks, mental toughness guide, and pro court accessories.
                </p>
                <button
                  type="button"
                  onClick={() => {
                    setIsCartOpen(false);
                    navigate('/extras-shop');
                  }}
                  className="bg-green-600 hover:bg-green-700 text-white font-semibold py-2.5 px-6 rounded-full text-sm shadow-sm transition cursor-pointer"
                >
                  Browse the Shop
                </button>
              </div>
            ) : (
              items.map((item) => (
                <div
                  key={item.cartItemId}
                  className="py-4 flex gap-4 items-center"
                  data-testid={`cart-item-${item.productId}`}
                >
                  <img
                    src={item.image}
                    alt={item.name}
                    loading="lazy"
                    className="w-20 h-20 rounded-xl object-cover bg-green-50 border border-gray-200 flex-shrink-0"
                  />
                  <div className="flex-1 min-w-0">
                    <h4 className="text-sm font-bold text-gray-900 truncate">{item.name}</h4>
                    {item.variant && (
                      <span className="inline-block mt-0.5 text-xs bg-green-50 text-green-800 px-2 py-0.5 rounded-md font-medium border border-green-200">
                        {item.variant}
                      </span>
                    )}
                    <div className="mt-1 text-sm font-bold text-green-700">
                      {formatPrice(item.price * item.quantity)}
                      {item.quantity > 1 && (
                        <span className="text-xs font-normal text-gray-500 ml-1">
                          ({formatPrice(item.price)} each)
                        </span>
                      )}
                    </div>

                    {/* Quantity Controls */}
                    <div className="mt-2.5 flex items-center justify-between">
                      <div className="inline-flex items-center border border-gray-300 rounded-lg bg-gray-50">
                        <button
                          type="button"
                          onClick={() => updateQuantity(item.cartItemId, item.quantity - 1)}
                          className="px-2.5 py-1 text-gray-700 hover:bg-gray-200 rounded-l-lg font-bold text-sm cursor-pointer"
                          aria-label={`Decrease quantity of ${item.name}`}
                          data-testid={`decrease-qty-${item.productId}`}
                        >
                          −
                        </button>
                        <span
                          className="px-3 py-1 text-xs font-bold text-gray-900"
                          data-testid={`cart-qty-${item.productId}`}
                        >
                          {item.quantity}
                        </span>
                        <button
                          type="button"
                          onClick={() => updateQuantity(item.cartItemId, item.quantity + 1)}
                          className="px-2.5 py-1 text-gray-700 hover:bg-gray-200 rounded-r-lg font-bold text-sm cursor-pointer"
                          aria-label={`Increase quantity of ${item.name}`}
                          data-testid={`increase-qty-${item.productId}`}
                        >
                          +
                        </button>
                      </div>

                      <button
                        type="button"
                        onClick={() => removeFromCart(item.cartItemId)}
                        className="text-xs font-semibold text-red-600 hover:text-red-700 hover:underline cursor-pointer"
                        data-testid={`remove-cart-item-${item.productId}`}
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Footer Summary & Checkout CTA */}
          {items.length > 0 && (
            <div className="border-t border-gray-200 bg-gray-50 p-5 space-y-3">
              {/* Promo Code Input */}
              <div className="bg-white rounded-xl p-3 border border-gray-200">
                <form onSubmit={handlePromoSubmit} className="flex gap-2">
                  <input
                    type="text"
                    value={promoCodeInput}
                    onChange={(e) => setPromoCodeInput(e.target.value.toUpperCase())}
                    placeholder="Promo / Free Shipping Code"
                    data-testid="cart-promo-input"
                    className="flex-1 px-3 py-1.5 rounded-lg border border-gray-300 text-xs font-mono uppercase focus:outline-none focus:ring-2 focus:ring-green-600"
                  />
                  {appliedPromoRule && (!promoCodeInput || promoCodeInput === appliedPromoRule.code) ? (
                    <button
                      type="button"
                      onClick={removePromoCode}
                      data-testid="cart-remove-promo-btn"
                      className="px-3 py-1.5 rounded-lg bg-red-50 hover:bg-red-100 text-red-700 border border-red-200 text-xs font-bold cursor-pointer"
                    >
                      Remove
                    </button>
                  ) : (
                    <button
                      type="submit"
                      data-testid="cart-apply-promo-btn"
                      className="px-3.5 py-1.5 rounded-lg bg-green-700 hover:bg-green-800 text-white text-xs font-bold cursor-pointer"
                    >
                      Apply
                    </button>
                  )}
                </form>
                {(promoFeedback?.message || promoFeedback?.text) && (
                  <p
                    className={`mt-1.5 text-[11px] font-semibold ${
                      promoFeedback.type === 'error' ? 'text-red-600' : 'text-green-700'
                    }`}
                    data-testid="cart-promo-feedback"
                  >
                    {promoFeedback.message || promoFeedback.text}
                  </p>
                )}
              </div>

              <div className="space-y-1.5 text-sm">
                <div className="flex justify-between text-gray-600">
                  <span>Subtotal</span>
                  <span className="font-semibold text-gray-900" data-testid="cart-subtotal">
                    {formatPrice(subtotal)}
                  </span>
                </div>
                {discountAmount > 0 && (
                  <div className="flex justify-between text-emerald-700 font-semibold" data-testid="cart-discount-row">
                    <span className="truncate pr-2">
                      Discount {discountSummary ? `(${discountSummary})` : ''}
                    </span>
                    <span data-testid="cart-discount">-{formatPrice(discountAmount)}</span>
                  </div>
                )}
                <div className="flex justify-between text-gray-600">
                  <span>Estimated Shipping</span>
                  <span className="font-semibold text-gray-900" data-testid="cart-shipping">
                    {shippingAmount === 0 ? 'FREE' : formatPrice(shippingAmount)}
                  </span>
                </div>
                <div className="flex justify-between text-gray-600">
                  <span>
                    Estimated {activeTaxZone?.tax_name || 'HST'} ({activeTaxZone?.tax_rate ?? 13}%)
                  </span>
                  <span className="font-semibold text-gray-900" data-testid="cart-tax">
                    {formatPrice(taxAmount)}
                  </span>
                </div>
                <div className="flex justify-between text-base font-extrabold text-green-900 pt-2 border-t border-gray-200">
                  <span>Estimated Total</span>
                  <span data-testid="cart-total">{formatPrice(totalAmount)}</span>
                </div>
              </div>

              <button
                type="button"
                onClick={handleProceedToCheckout}
                className="w-full bg-yellow-400 hover:bg-yellow-500 text-green-950 font-extrabold py-3.5 px-6 rounded-xl shadow-md transition duration-200 flex items-center justify-center gap-2 text-base cursor-pointer"
                data-testid="proceed-to-checkout-btn"
              >
                <span>Proceed to PayPal Checkout</span>
                <span>→</span>
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CartDrawer;
